import { Layers, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CopyDetailsEditor } from "@/components/catalog/CopyDetailsEditor";
import { JournalHierarchyFields } from "@/components/catalog/JournalHierarchyFields";
import { RequiredLabel } from "@/components/catalog/RequiredLabel";
import type { CreateCatalogRequest } from "@/lib/api/catalog";
import type { JournalCatalogOptions } from "@/pages/admin/catalog/useJournalCatalogOptions";
import type { JournalHoldingEntry } from "@/pages/admin/catalog/journal-catalog-form";

type ItemDefaults = CreateCatalogRequest["itemDefaults"];

interface JournalHoldingCardProps {
  index: number;
  holding: JournalHoldingEntry;
  canRemove: boolean;
  errors: Record<string, string>;
  options: JournalCatalogOptions;
  itemDefaults: ItemDefaults;
  onChange: (holding: JournalHoldingEntry) => void;
  onRemove: () => void;
}

export function JournalHoldingCard({
  index,
  holding,
  canRemove,
  errors,
  options,
  itemDefaults,
  onChange,
  onRemove,
}: JournalHoldingCardProps) {
  const isManual = options.settings.accessionNumberMode === "MANUAL";
  return (
    <Card className="overflow-hidden bg-white shadow-sm">
      <div className="space-y-5 p-6">
        <div className="flex items-start justify-between gap-3">
          <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
            <Layers className="h-5 w-5 text-primary" />
            Volume {index + 1}
          </h2>
          {canRemove ? (
            <Button type="button" variant="outline" size="sm" onClick={onRemove}>
              <Trash2 className="mr-1 h-4 w-4" />
              Remove
            </Button>
          ) : null}
        </div>
        <JournalHierarchyFields
          value={holding.journal}
          onChange={(journal) => onChange({ ...holding, journal })}
          errors={{
            volumeNumber:
              errors[`journalHoldings.${index}.volumeNumber`] ||
              (index === 0 ? errors.volumeNumber : undefined),
          }}
          embedded
          idPrefix={`holding-${holding.key}`}
        />
        <div>
          <RequiredLabel htmlFor={`holding-copies-${holding.key}`} required>
            Number of copies
          </RequiredLabel>
          <Input
            id={`holding-copies-${holding.key}`}
            type="number"
            min={1}
            className="mt-1.5 max-w-xs"
            value={holding.copies}
            onChange={(event) => {
              const copies = Math.max(1, Number.parseInt(event.target.value, 10) || 1);
              onChange({
                ...holding,
                copies,
                itemCopies: Array.from({ length: copies }, (_, i) => holding.itemCopies[i] ?? {}),
              });
            }}
          />
        </div>
        <div>
          <Label className="mb-3 block text-sm font-medium text-gray-900">Copy details</Label>
          <CopyDetailsEditor
            copies={holding.copies}
            copyData={holding.itemCopies}
            onCopyDataChange={(itemCopies) => onChange({ ...holding, itemCopies })}
            conditions={options.conditions}
            itemStatuses={options.itemStatuses}
            locations={options.locations}
            branches={options.branches}
            isManualMode={isManual}
            defaultCondition={itemDefaults.conditionCode}
            defaultStatus={itemDefaults.statusCode || "AVAILABLE"}
            defaultLocationId={itemDefaults.locationId}
            defaultBranchId={itemDefaults.branchId}
            defaultAcquisitionMode={itemDefaults.acquisitionMode}
            highlightCopyAcquisition={Array.from({ length: holding.copies }, (_, copyIndex) =>
              Boolean(
                errors[`journalHoldings.${index}.itemCopies[${copyIndex}].acquisitionMode`] ||
                  errors.acquisitionMode
              )
            )}
          />
        </div>
      </div>
    </Card>
  );
}
